import SvgIcon from "@/components/common/SvgIcon";
import { Avatar, Badge, Button, Image, Typography } from "@/components/kit";
import { Table } from "@/components/kit/Table/Table";
import Tooltip from "@/components/kit/Tooltip";
import type { TBadgeProps } from "@/components/kit/Badge";
import { FC, ReactNode, useMemo } from "react";
import { TOrganization } from "../../types";
import useDetectFlag from "../../hooks/useDetectFlag";
import { useNavigate } from "react-router-dom";
import useLoginToTrio from "../../hooks/useLoginToTrio";
import { Progress } from "@material-tailwind/react";
import moment from "moment-timezone";
import {
  getOrganizationLicenseRowUi,
  TLicenseBadgeVariant,
} from "./organizationLicenseRowUi";

interface ITableRowProps {
  organization: TOrganization;
}

function badgePropsForVariant(variant: TLicenseBadgeVariant): {
  label: string;
  state: TBadgeProps["state"];
  tooltip?: string;
  rightElement?: ReactNode;
} {
  switch (variant) {
    case "expired":
      return {
        label: "Expired",
        state: "danger",
        tooltip:
          "License expired. Device management features may be restricted.",
      };
    case "not_configured":
      return {
        label: "Not configured",
        state: "notice",
      };
    case "payment_failed":
      return {
        label: "Payment failed",
        state: "warning",
        rightElement: (
          <SvgIcon
            iconName="DollarMinimalistic"
            fill="fill-orange-700"
            className="w-3 h-3"
          />
        ),
      };
    case "over_capacity":
      return {
        label: "Over capacity",
        state: "pause",
      };
    case "almost_full":
      return {
        label: "Almost full",
        state: "warning",
        tooltip: "You are close to your license limit. Consider upgrading.",
      };
    default:
      return {
        label: "Active",
        state: "success",
        rightElement: <div className="w-2 h-2 bg-green-500 rounded-full" />,
      };
  }
}

const OrganizationRow: FC<ITableRowProps> = ({ organization }) => {
  const country = useDetectFlag(organization.country ?? "");
  const navigate = useNavigate();
  const { handleLogin, isPending } = useLoginToTrio();

  const licenseUi = useMemo(
    () => getOrganizationLicenseRowUi(organization),
    [organization],
  );

  const badgeUi = badgePropsForVariant(licenseUi.badgeVariant);

  if (!organization) return null;

  const { id, name, owner, userCount, country: orgCountry } = organization;
  const portalHost = organization.url?.replace(/^https?:\/\//, "") ?? "";
  const planLabel = organization.billingDetails?.plan
    ?.replaceAll("_", " ")
    .toLowerCase();

  const badgeContent = (
    <Badge size="sm" state={badgeUi.state}>
      <div className="flex items-center gap-1">
        {badgeUi.rightElement}
        {badgeUi.label}
      </div>
    </Badge>
  );

  return (
    <Table.RowBody
      onClick={() => navigate(`/organizations/${organization.id}`)}
      key={id}
      className="flex w-full h-auto border-b group hover:bg-gray-1100 cursor-pointer transition-colors"
    >
      <Table.Cell
        name="Name"
        index={0}
        className="flex gap-[10px] items-center !py-2 !px-4"
      >
        <div className="w-6 h-6">
          {organization?.logo && (
            <Image width={24} height={24} src={organization?.logo} />
          )}
        </div>

        <Typography
          hasTitle
          variant="BodyPlus2-medium"
          className="truncate !font-bold flex-1"
          color="!text-text-600"
        >
          {name}
        </Typography>
      </Table.Cell>
      <Table.Cell
        name="Portal link"
        index={1}
        className="flex items-center gap-2"
        onClick={(e) => {
          e.stopPropagation();
          handleLogin(organization?.id);
        }}
      >
        {portalHost ? (
          <>
            <Typography
              hasTitle
              variant="Caption1-Regular"
              className={` truncate !text-blue-700 `}
            >
              {portalHost}
            </Typography>
            <SvgIcon
              iconName="ArrowActionSquare"
              fill="fill-[#105FCF]"
              width={12}
              height={12}
            />
          </>
        ) : null}
      </Table.Cell>
      <Table.Cell
        name="Region"
        className="h-auto flex items-center gap-3"
        index={2}
      >
        <div
          className="!text-[24px]"
          style={{ fontFamily: "Twemoji Country Flags" }}
        >
          {country?.flag}
        </div>
        <Typography variant="Caption1-Regular">{orgCountry}</Typography>
      </Table.Cell>
      <Table.Cell name="Owner" index={3} className=" flex items-center gap-2">
        <Avatar size="sm" />
        <Typography variant="Caption1-Regular">
          {[owner?.firstName, owner?.lastName].filter(Boolean).join(" ")}
        </Typography>
      </Table.Cell>
      <Table.Cell name="Admins" index={4}>
        {typeof userCount === "number" ? userCount : null}
      </Table.Cell>
      <Table.Cell name="License status" index={5}>
        {badgeUi.tooltip ? (
          <Tooltip
            text={badgeUi.tooltip}
            isTruncate={false}
            className="!max-w-[280px] !z-50 !px-3 !py-2"
          >
            <span className="inline-flex">{badgeContent}</span>
          </Tooltip>
        ) : (
          badgeContent
        )}
      </Table.Cell>
      <Table.Cell name="License usage" index={6}>
        {licenseUi.showUsageRow ? (
          <Tooltip
            text={licenseUi.usageTooltip ?? ""}
            hidden={!licenseUi.usageTooltip}
            isTruncate={false}
            className="!max-w-[300px] !w-[300px] !min-w-[300px] !z-50 !px-3 !py-2"
          >
            <div className="flex items-center gap-2 min-w-0 pr-2">
              <div className="flex-1 min-w-[72px] max-w-[140px]">
                <Progress
                  size="sm"
                  color={licenseUi.progressColor}
                  value={licenseUi.progressValue}
                  placeholder="license_usage"
                />
              </div>
              <Typography
                variant="Caption1-Regular"
                className="shrink-0 text-gray-700 tabular-nums"
              >
                {licenseUi.usedDevices}/{licenseUi.totalLicenses}
              </Typography>
            </div>
          </Tooltip>
        ) : null}
      </Table.Cell>
      <Table.Cell name="Available" index={7}>
        {typeof licenseUi.available === "number" ? (
          <Typography
            variant="Caption1-Regular"
            className="tabular-nums text-gray-700"
          >
            {licenseUi.available}
          </Typography>
        ) : null}
      </Table.Cell>
      <Table.Cell name="Active plan" index={8}>
        {planLabel ? (
          <Typography
            color="text-gray-600"
            className="capitalize"
            variant="Caption1-Regular"
          >
            {planLabel}
          </Typography>
        ) : null}
      </Table.Cell>
      <Table.Cell name="Added date" index={9}>
        {organization.createdAt ? (
          <Typography
            color="text-gray-600"
            className="capitalize"
            variant="Caption1-Regular"
          >
            {moment(organization.createdAt).format("D MMM, YYYY @ h:mm A")}
          </Typography>
        ) : null}
      </Table.Cell>
      <Table.Cell name="" justify="center" index={10}>
        <Button
          isLoading={isPending}
          onClick={(e) => {
            e.stopPropagation();
            if (licenseUi.isNotConfigured) {
              navigate(`/organizations/${organization.id}?tab=overview`);
            } else {
              handleLogin(organization?.id);
            }
          }}
          className="!text-xs !h-6 !font-bold !flex !gap-3"
          variant="outlined"
        >
          <span className="!font-bold">
            {licenseUi.isNotConfigured ? "Setup" : "Open"}
          </span>
          <SvgIcon
            iconName="ArrowActionSquare"
            fill="fill-gray-800"
            width={12}
            height={12}
          />
        </Button>
      </Table.Cell>
    </Table.RowBody>
  );
};

export default OrganizationRow;
